/**
 * Quotations — `docs/API.md` §7.
 *
 * Fully implemented server-side now: CRUD, revise, submit-for-approval and
 * send all live in `salescrmapi/src/modules/quotations`. The approval
 * decision itself is `POST /approvals/{request_id}/decide` — see
 * `api/approvals.api.js` — a separate, entity-agnostic endpoint, not a
 * quotations route.
 *
 * No PDF generation (§11 isn't built): `pdf_url` is always null and `send`
 * never attaches a file — see the module's own doc comment on the API side.
 */
import apiClient, { ifMatch, idempotencyKey } from './axiosClient.js';

/** @param {object} [params] any of listQuotationsQuery's fields, e.g. `{ q, status, account_id, opportunity_id }` */
export async function listQuotations(params = {}) {
  const { data } = await apiClient.get('/quotations', { params });
  return data;
}

export async function getQuotation(id) {
  const { data } = await apiClient.get(`/quotations/${id}`);
  return data;
}

/** @param {object} payload at minimum `{ account_id, currency, items[] }` */
export async function createQuotation(payload) {
  const { data } = await apiClient.post('/quotations', payload, { headers: idempotencyKey() });
  return data;
}

export async function updateQuotation(id, patch, rowVersion) {
  const { data } = await apiClient.patch(`/quotations/${id}`, patch, { headers: ifMatch(rowVersion) });
  return data;
}

export async function deleteQuotation(id, { reason, rowVersion } = {}) {
  await apiClient.delete(`/quotations/${id}`, {
    data: { reason, ...(rowVersion != null ? { row_version: rowVersion } : {}) },
  });
}

/** @param {string} id @param {{reason:string, carry_items?:boolean}} payload */
export async function reviseQuotation(id, payload) {
  const { data } = await apiClient.post(`/quotations/${id}/revise`, payload, { headers: idempotencyKey() });
  return data;
}

/** @param {string} id @param {{note?:string}} payload */
export async function submitForApproval(id, payload = {}) {
  const { data } = await apiClient.post(`/quotations/${id}/submit-for-approval`, payload);
  return data;
}

/** @param {string} id @param {{to:string[], cc?:string[], subject?:string, body_html?:string}} payload */
export async function sendQuotation(id, payload) {
  const { data } = await apiClient.post(`/quotations/${id}/send`, payload, { headers: idempotencyKey() });
  return data;
}
